import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../components/Input';
import Dropdown from '../../../../../components/Dropdown';
import Switch from '../../../../../components/Switch';
import PhoneInput from '../../../../../components/PhoneInput';
import { AddEdit } from '../../../../../interfaces/stockist.interface';
import { Validation } from '../../../../../interfaces/common';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';

interface StockistDetailsTabProps {
  edit: boolean;
  view: boolean;
  zones: any[];
  cities: any[];
  allCities: any[];
  states: any[];
  allStates: any[];
  formData: AddEdit;
  validation: Validation;
  dropdownSearchData: any;
  onDropdownSearchChange: (event: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (event: any, name: string) => void;
  onInputChange: (event: any, name: string) => void;
}

const StockistDetailsTab: React.FC<StockistDetailsTabProps> = ({
  edit,
  view,
  zones,
  cities,
  allCities,
  states,
  allStates,
  formData,
  validation,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
}) => {
  const { t } = useTranslation();

  const getErrorMessage = (field: string) => {
    if (validation.validate) return undefined;
    return validation.fields.find((f) => f.field === field)?.message;
  };

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">1. {t('stockists.addEdit.pageTexts.form.sections.stockistDetails')}</h6>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.name.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.name}
              name={VALIDATION_FIELD_MAPPING.name}
              value={formData.name}
              placeholder={t('stockists.addEdit.pageTexts.form.name.placeholder')}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.name)}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
              onKeyDown={onKeyDown}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.name)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.name)}
              disabled={view}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              label={t('stockists.addEdit.pageTexts.form.website.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.website}
              name={VALIDATION_FIELD_MAPPING.website}
              value={formData.website}
              placeholder={t('stockists.addEdit.pageTexts.form.website.placeholder')}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.website)}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.website)}
              onKeyDown={onKeyDown}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.website)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.website)}
              disabled={view}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.gst.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.gstNumber}
              name={VALIDATION_FIELD_MAPPING.gstNumber}
              value={formData.gstNumber}
              placeholder={t('stockists.addEdit.pageTexts.form.gst.placeholder')}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.gstNumber)}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.gstNumber)}
              onKeyDown={onKeyDown}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.gstNumber)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.gstNumber)}
              disabled={view}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="input-group input-group-merge">
            <div className="form-floating form-floating-outline">
              <Input
                required
                label={t('stockists.addEdit.pageTexts.form.email.label')}
                type="email"
                id={VALIDATION_FIELD_MAPPING.email}
                name={VALIDATION_FIELD_MAPPING.email}
                value={formData.email}
                placeholder={t('stockists.addEdit.pageTexts.form.email.placeholder')}
                onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.email)}
                onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.email)}
                onKeyDown={onKeyDown}
                error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.email)}
                errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.email)}
                disabled={view}
              />
            </div>
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">2. {t('stockists.addEdit.pageTexts.form.sections.primaryContactDetails')}</h6>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.primaryContactPerson.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.primaryContactPerson}
              name={VALIDATION_FIELD_MAPPING.primaryContactPerson}
              value={formData.primaryContactPerson}
              placeholder={t('stockists.addEdit.pageTexts.form.primaryContactPerson.placeholder')}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.primaryContactPerson)}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.primaryContactPerson)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactPerson)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactPerson)}
              disabled={view}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              required
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.primaryContactNumber}
              name={VALIDATION_FIELD_MAPPING.primaryContactNumber}
              label={t('stockists.addEdit.pageTexts.form.primaryContactNumber.label')}
              placeholder={t('stockists.addEdit.pageTexts.form.primaryContactNumber.placeholder')}
              value={formData.primaryContactNumber}
              onChange={onInputChange}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.primaryContactNumber)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactNumber)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactNumber)}
              disabled={view}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="input-group input-group-merge">
            <div className="form-floating form-floating-outline">
              <Input
                required
                label={t('stockists.addEdit.pageTexts.form.primaryContactEmail.label')}
                type="email"
                id={VALIDATION_FIELD_MAPPING.primaryContactEmail}
                name={VALIDATION_FIELD_MAPPING.primaryContactEmail}
                value={formData.primaryContactEmail}
                placeholder={t('stockists.addEdit.pageTexts.form.primaryContactEmail.placeholder')}
                onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.primaryContactEmail)}
                onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.primaryContactEmail)}
                error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactEmail)}
                errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.primaryContactEmail)}
                disabled={view}
              />
            </div>
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">3. {t('stockists.addEdit.pageTexts.form.sections.addressDetails')}</h6>

        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.address.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.address}
              name={VALIDATION_FIELD_MAPPING.address}
              value={formData.address}
              placeholder={t('stockists.addEdit.pageTexts.form.address.placeholder')}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.address)}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.address)}
              onKeyDown={onKeyDown}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.address)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.address)}
              disabled={view}
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              label={t('stockists.addEdit.pageTexts.form.zone.label')}
              required
              noSearch
              values={formData.zone ? [formData.zone] : []}
              options={(zones || []).map((z: any) => ({
                label: z.name,
                value: String(z.id),
              }))}
              allOptions={(zones || []).map((z: any) => ({
                label: z.name,
                value: String(z.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.zone}
              name={VALIDATION_FIELD_MAPPING.zone}
              onChange={onInputChange}
              onBlur={onInputBlur}
              placeholder={t('stockists.addEdit.pageTexts.form.zone.placeholder')}
              disabled={view}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.zone)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.zone)}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              label={t('stockists.addEdit.pageTexts.form.city.label')}
              required
              values={formData.city ? [formData.city] : []}
              options={(cities || []).map((city: any) => ({
                label: city.name,
                value: String(city.id),
              }))}
              allOptions={(allCities || []).map((city: any) => ({
                label: city.name,
                value: String(city.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.city}
              name={VALIDATION_FIELD_MAPPING.city}
              searchText={dropdownSearchData.city}
              onSearch={onDropdownSearchChange}
              onChange={onInputChange}
              onBlur={onInputBlur}
              placeholder={t('stockists.addEdit.pageTexts.form.city.placeholder')}
              disabled={view}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.city)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.city)}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              label={t('stockists.addEdit.pageTexts.form.state.label')}
              required
              values={formData.state ? [formData.state] : []}
              options={(states || []).map((state: any) => ({
                label: state.name,
                value: String(state.id),
              }))}
              allOptions={(allStates || []).map((state: any) => ({
                label: state.name,
                value: String(state.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.state}
              name={VALIDATION_FIELD_MAPPING.state}
              searchText={dropdownSearchData.state}
              onSearch={onDropdownSearchChange}
              onChange={onInputChange}
              onBlur={onInputBlur}
              placeholder={t('stockists.addEdit.pageTexts.form.state.placeholder')}
              disabled={view}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.state)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.state)}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.addEdit.pageTexts.form.pincode.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.pincode}
              name={VALIDATION_FIELD_MAPPING.pincode}
              value={formData.pincode}
              placeholder={t('stockists.addEdit.pageTexts.form.pincode.placeholder')}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.pincode)}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.pincode)}
              onKeyDown={onKeyDown}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.pincode)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.pincode)}
              disabled={view}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">4. {t('stockists.addEdit.pageTexts.form.sections.otherInformation')}</h6>

        <div className="col-md-12 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              label={
                formData.status
                  ? t('stockists.addEdit.pageTexts.form.status.activeLabel')
                  : t('stockists.addEdit.pageTexts.form.status.inActiveLabel')
              }
              checked={formData.status}
              name={VALIDATION_FIELD_MAPPING.status}
              disabled={view}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default StockistDetailsTab;
